import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { 
  X, Download, MessageSquare, Flag, Brain, Briefcase, Heart, TrendingUp, 
  Target, Star, Clock, AlertTriangle, ChevronRight, Activity, Users,
  Sparkles, Calendar, BarChart3, ArrowUp, ArrowDown, Minus, History,
  FileText, Clipboard, Check, RefreshCw, Eye, GitCompare, Send,
  User, AlertCircle
} from 'lucide-react';
import { DESIGN } from '../DesignSystem';
import { COGNITIVE_LANES } from '../../mock';
import { generateBrief, getBriefHistory, TOPIC_CATEGORIES_CONFIG } from './briefData';
import { line } from 'd3-shape';
import { toast } from 'sonner';
import EnhancedFlagModal from './EnhancedFlagModal';

// ================================================
// SPARKLINE COMPONENT
// ================================================
const Sparkline = ({ data, color = '#00C9A7', width = 80, height = 24 }) => {
  if (!data || data.length === 0) return null;
  
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  
  const points = data.map((value, index) => ({
    x: (index / (data.length - 1)) * width,
    y: height - ((value - min) / range) * height
  }));
  
  const lineGenerator = line()
    .x(d => d.x)
    .y(d => d.y);
  
  const pathD = lineGenerator(points);
  
  return (
    <svg width={width} height={height} className="overflow-visible">
      <path
        d={pathD}
        fill="none"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle
        cx={points[points.length - 1].x}
        cy={points[points.length - 1].y}
        r="3"
        fill={color}
      />
    </svg>
  );
};

// ================================================
// TREND INDICATOR
// ================================================
const TrendIndicator = ({ trend, size = 'sm' }) => {
  const sizeMap = { sm: 12, md: 16, lg: 20 };
  const iconSize = sizeMap[size] || 12;
  
  if (trend === 'up') {
    return <ArrowUp className={`w-${iconSize/4} h-${iconSize/4}`} style={{ color: '#10B981' }} />;
  } else if (trend === 'down') {
    return <ArrowDown className={`w-${iconSize/4} h-${iconSize/4}`} style={{ color: '#EF4444' }} />;
  }
  return <Minus className={`w-${iconSize/4} h-${iconSize/4}`} style={{ color: '#6B7280' }} />;
};

// ================================================
// SECTION HEADER
// ================================================
const SectionHeader = ({ icon: Icon, title, color = '#00C9A7' }) => (
  <div className="flex items-center gap-3 mb-4">
    <div 
      className="w-8 h-8 rounded-lg flex items-center justify-center"
      style={{ backgroundColor: `${color}15` }}
    >
      <Icon className="w-4 h-4" style={{ color }} />
    </div>
    <h3 className="text-sm font-bold uppercase tracking-wider" style={{ color: DESIGN.colors.textMuted }}>
      {title}
    </h3>
  </div>
);

// ================================================
// AI INSIGHT BADGE
// ================================================
const AIInsightBadge = () => (
  <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-amber-50 border border-amber-200">
    <Sparkles className="w-3 h-3 text-amber-500" />
    <span className="text-xs font-semibold text-amber-700">AI Insight</span>
  </div>
);

// ================================================
// SECTION 1: COGNITIVE STATUS
// ================================================
const CognitiveStatusSection = ({ data }) => {
  const { laneAlignmentScore, laneAlignmentTrend, levelAppropriateWork, primaryLane, activeSecondaryLane, daysInSecondaryLane, aiSummary } = data;
  
  return (
    <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
      <SectionHeader icon={Brain} title="Cognitive Status" color="#8B5CF6" />
      
      <div className="grid grid-cols-2 gap-6 mb-6">
        <div className="p-4 rounded-xl bg-gray-50">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-600">Lane Alignment</span>
            <TrendIndicator trend={laneAlignmentTrend} />
          </div>
          <div className="flex items-baseline gap-1">
            <span className="text-3xl font-black" style={{ color: laneAlignmentScore >= 70 ? '#10B981' : '#F59E0B' }}>
              {laneAlignmentScore}%
            </span>
          </div>
          <div className="flex items-center gap-2 mt-2">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: primaryLane?.color }} />
            <span className="text-xs text-gray-500">Primary: {primaryLane?.name}</span>
          </div>
        </div>
        
        <div className="p-4 rounded-xl bg-gray-50">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-600">Level-Appropriate Work</span>
          </div>
          <div className="flex items-baseline gap-1">
            <span className="text-3xl font-black" style={{ color: levelAppropriateWork >= 70 ? '#10B981' : '#F59E0B' }}>
              {levelAppropriateWork}%
            </span>
          </div>
          {levelAppropriateWork < 70 && (
            <p className="text-xs text-amber-600 mt-2">Below optimal level utilization</p>
          )}
        </div>
      </div>
      
      <div className="p-4 rounded-xl bg-gradient-to-r from-violet-50 to-purple-50 border border-violet-100">
        <div className="flex items-start gap-3">
          <AIInsightBadge />
        </div>
        <p className="text-sm text-gray-700 leading-relaxed mt-3">{aiSummary}</p>
      </div>
    </div>
  );
};

// ================================================
// SECTION 2: WORKLOAD & CAPACITY
// ================================================
const WorkloadCapacitySection = ({ data }) => {
  const { currentLoad, maxLoad, loadPercentage, activeTasks, overdueTasks, avgCompletionTime, avgCompletionTimeLastMonth, loadTrend, alert } = data;
  
  const loadColor = loadPercentage >= 90 ? '#EF4444' : loadPercentage >= 75 ? '#F59E0B' : '#10B981';
  
  return (
    <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
      <SectionHeader icon={Briefcase} title="Workload & Capacity" color="#3B82F6" />
      
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-600">Current Load</span>
          <span className="text-sm font-bold" style={{ color: loadColor }}>{currentLoad}/{maxLoad} tasks</span>
        </div>
        <div className="h-4 bg-gray-100 rounded-full overflow-hidden">
          <div 
            className="h-full rounded-full transition-all duration-500"
            style={{ 
              width: `${loadPercentage}%`,
              backgroundColor: loadColor,
              boxShadow: loadPercentage >= 80 ? `0 0 10px ${loadColor}50` : 'none'
            }}
          />
        </div>
        <div className="flex items-center justify-between mt-2">
          <span className="text-2xl font-black" style={{ color: loadColor }}>{loadPercentage}%</span>
          <Sparkline data={loadTrend} color={loadColor} />
        </div>
      </div>
      
      <div className="grid grid-cols-4 gap-4 mb-4">
        <div className="text-center p-3 rounded-xl bg-gray-50">
          <p className="text-2xl font-black text-gray-900">{activeTasks}</p>
          <p className="text-xs text-gray-500">Active</p>
        </div>
        <div className="text-center p-3 rounded-xl bg-gray-50">
          <p className="text-2xl font-black" style={{ color: overdueTasks > 0 ? '#EF4444' : '#10B981' }}>{overdueTasks}</p>
          <p className="text-xs text-gray-500">Overdue</p>
        </div>
        <div className="text-center p-3 rounded-xl bg-gray-50">
          <p className="text-lg font-bold text-gray-900">{avgCompletionTime}</p>
          <p className="text-xs text-gray-500">Avg Time</p>
        </div>
        <div className="text-center p-3 rounded-xl bg-gray-50">
          <p className="text-lg font-bold text-gray-500">{avgCompletionTimeLastMonth}</p>
          <p className="text-xs text-gray-500">Last Month</p>
        </div>
      </div>
      
      {alert && (
        <div className="p-4 rounded-xl bg-amber-50 border border-amber-200 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-amber-800">{alert.message}</p>
        </div>
      )}
    </div>
  );
};

// ================================================
// SECTION 3: BURNOUT RISK INTELLIGENCE
// ================================================
const BurnoutRiskSection = ({ data }) => {
  const { score, trend, weeksAtLevel, topDrivers, trendData, aiInterpretation, suggestedOpener } = data;
  
  const riskColor = score >= 70 ? '#EF4444' : score >= 50 ? '#F59E0B' : '#10B981';
  const riskLabel = score >= 70 ? 'High Risk' : score >= 50 ? 'Elevated' : 'Healthy';
  
  return (
    <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
      <SectionHeader icon={Heart} title="Burnout Risk Intelligence" color="#F472B6" />
      
      <div className="flex items-start gap-6 mb-6">
        <div className="relative">
          <svg width="100" height="100" className="transform -rotate-90">
            <circle cx="50" cy="50" r="40" stroke="#E5E7EB" strokeWidth="8" fill="none" />
            <circle cx="50" cy="50" r="40" stroke={riskColor} strokeWidth="8" fill="none"
              strokeDasharray={`${(score / 100) * 251.2} 251.2`} strokeLinecap="round" />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className="text-2xl font-black" style={{ color: riskColor }}>{score}</span>
            <span className="text-xs text-gray-500">Risk</span>
          </div>
        </div>
        
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <span className="px-3 py-1 rounded-full text-xs font-bold"
              style={{ backgroundColor: `${riskColor}15`, color: riskColor }}>
              {riskLabel}
            </span>
            <TrendIndicator trend={trend} />
          </div>
          <p className="text-sm text-gray-600 mb-3">At this level for {weeksAtLevel} week{weeksAtLevel > 1 ? 's' : ''}</p>
          
          <div className="space-y-2">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Top Drivers</p>
            {topDrivers.map((driver, idx) => (
              <div key={driver.id} className="flex items-center gap-2 p-2 rounded-lg bg-gray-50">
                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: riskColor }} />
                <span className="text-sm text-gray-700">{driver.name}</span>
              </div>
            ))}
          </div>
        </div>
        
        <div className="flex flex-col items-center">
          <p className="text-xs text-gray-500 mb-2">8-Week Trend</p>
          <Sparkline data={trendData} color={riskColor} width={100} height={40} />
        </div>
      </div>
      
      <div className="p-4 rounded-xl bg-gradient-to-r from-pink-50 to-rose-50 border border-pink-100 mb-4">
        <AIInsightBadge />
        <p className="text-sm text-gray-700 leading-relaxed mt-3">{aiInterpretation}</p>
      </div>
      
      {suggestedOpener && (
        <div className="p-4 rounded-xl bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200">
          <p className="text-xs font-bold text-emerald-700 uppercase tracking-wide mb-2">Suggested Check-in</p>
          <p className="text-sm text-emerald-800 italic">{suggestedOpener}</p>
        </div>
      )}
    </div>
  );
};

// ================================================
// SECTION 4: PERFORMANCE CONTEXT
// ================================================
const PerformanceContextSection = ({ data }) => {
  const { onTimeRate, onTimeRateLastMonth, onTimeRateTrend, projects, wins, phasesCompleted, phasesMissed } = data;
  
  return (
    <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
      <SectionHeader icon={BarChart3} title="Performance Context" color="#10B981" />
      
      <div className="flex items-center justify-between p-4 rounded-xl bg-gray-50 mb-4">
        <div>
          <p className="text-sm text-gray-600 mb-1">On-Time Completion Rate</p>
          <div className="flex items-center gap-3">
            <span className="text-3xl font-black" style={{ color: onTimeRate >= 80 ? '#10B981' : '#F59E0B' }}>
              {onTimeRate}%
            </span>
            <TrendIndicator trend={onTimeRateTrend} />
            <span className="text-sm text-gray-400">vs {onTimeRateLastMonth}% last month</span>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-center">
            <p className="text-xl font-bold text-gray-900">{phasesCompleted}</p>
            <p className="text-xs text-gray-500">Phases Done</p>
          </div>
          <div className="text-center">
            <p className="text-xl font-bold" style={{ color: phasesMissed > 0 ? '#F59E0B' : '#10B981' }}>{phasesMissed}</p>
            <p className="text-xs text-gray-500">Missed</p>
          </div>
        </div>
      </div>
      
      {projects.length > 0 && (
        <div className="mb-4">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Contributing To</p>
          <div className="space-y-2">
            {projects.map((project, idx) => (
              <div key={idx} className="flex items-center justify-between p-3 rounded-xl bg-gray-50">
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: project.healthScore >= 70 ? '#10B981' : '#F59E0B' }} />
                  <span className="text-sm font-medium text-gray-800">{project.name}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-gray-500">{project.contribution}</span>
                  <span className="text-sm font-bold"
                    style={{ color: project.healthScore >= 70 ? '#10B981' : '#F59E0B' }}>
                    {project.healthScore}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {wins.length > 0 && (
        <div className="p-4 rounded-xl bg-gradient-to-r from-emerald-50 to-green-50 border border-emerald-100">
          <div className="flex items-center gap-2 mb-3">
            <Star className="w-4 h-4 text-emerald-500" />
            <p className="text-xs font-bold text-emerald-700 uppercase tracking-wide">Notable Wins</p>
          </div>
          <ul className="space-y-2">
            {wins.map((win, idx) => (
              <li key={idx} className="flex items-start gap-2 text-sm text-emerald-800">
                <ChevronRight className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
                {win}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

// ================================================
// SECTION 5: GROWTH & DEVELOPMENT
// ================================================
const GrowthDevelopmentSection = ({ data, memberName }) => {
  const { currentLevel, performingAboveLevel, suggestedLevel, supportingLaneUsage, aiInsight } = data;
  
  const levelDetails = {
    'L1': { name: 'DO', color: '#6366F1' },
    'L2': { name: 'RUN', color: '#8B5CF6' },
    'L3': { name: 'IMPROVE', color: '#A855F7' },
    'L4': { name: 'DECIDE + LEAD', color: '#C026D3' }
  };
  
  return (
    <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
      <SectionHeader icon={TrendingUp} title="Growth & Development" color="#8B5CF6" />
      
      <div className="flex items-center gap-4 p-4 rounded-xl bg-gray-50 mb-4">
        <div className="w-16 h-16 rounded-xl flex flex-col items-center justify-center"
          style={{ backgroundColor: levelDetails[currentLevel]?.color,
            boxShadow: `0 4px 15px ${levelDetails[currentLevel]?.color}40` }}>
          <span className="text-xl font-black text-white">{currentLevel}</span>
          <span className="text-xs font-bold text-white/80">{levelDetails[currentLevel]?.name}</span>
        </div>
        <div className="flex-1">
          <p className="text-sm text-gray-600">Current Level</p>
          {performingAboveLevel && (
            <div className="flex items-center gap-2 mt-2">
              <div className="px-3 py-1 rounded-full text-xs font-bold"
                style={{ backgroundColor: '#10B98115', color: '#10B981' }}>
                <TrendingUp className="w-3 h-3 inline mr-1" />
                Performing Above Level
              </div>
              <span className="text-sm text-gray-600">→ {suggestedLevel} Ready?</span>
            </div>
          )}
        </div>
      </div>
      
      {supportingLaneUsage.length > 0 && (
        <div className="mb-4">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Supporting Lane Development</p>
          <div className="space-y-2">
            {supportingLaneUsage.map((item, idx) => (
              <div key={idx} className="flex items-center gap-3">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.lane?.color }} />
                <span className="text-sm text-gray-700 flex-1">{item.lane?.name}</span>
                <span className="text-xs font-medium text-gray-500">{item.level}</span>
                <div className="w-24 h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div className="h-full rounded-full"
                    style={{ width: `${item.usagePercentage}%`, backgroundColor: item.lane?.color }} />
                </div>
                <span className="text-xs text-gray-500 w-8">{item.usagePercentage}%</span>
              </div>
            ))}
          </div>
        </div>
      )}
      
      <div className="p-4 rounded-xl bg-gradient-to-r from-violet-50 to-purple-50 border border-violet-100">
        <AIInsightBadge />
        <p className="text-sm text-gray-700 leading-relaxed mt-3">{aiInsight}</p>
      </div>
    </div>
  );
};

// ================================================
// SECTION 6: CONVERSATION TOPICS
// ================================================
const ConversationTopicsSection = ({ topics }) => {
  const iconMap = { Briefcase, Target, TrendingUp, Heart, Star };
  
  return (
    <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
      <SectionHeader icon={MessageSquare} title="Suggested Conversation Topics" color="#00C9A7" />
      
      <div className="space-y-3">
        {topics.map((topic, idx) => {
          const category = TOPIC_CATEGORIES_CONFIG[topic.category];
          const Icon = iconMap[category?.icon] || Target;
          
          return (
            <div key={idx} className="flex items-start gap-4 p-4 rounded-xl transition-all hover:shadow-md"
              style={{ backgroundColor: `${category?.color}08`, borderLeft: `4px solid ${category?.color}` }}>
              <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: `${category?.color}15` }}>
                <Icon className="w-5 h-5" style={{ color: category?.color }} />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs font-bold uppercase tracking-wide" style={{ color: category?.color }}>
                    {category?.label}
                  </span>
                  {topic.priority === 'high' && (
                    <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-red-100 text-red-600">Priority</span>
                  )}
                </div>
                <p className="text-sm text-gray-700 leading-relaxed">{topic.text}</p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ================================================
// EXPORT BRIEF MODAL
// ================================================
const ExportBriefModal = ({ isOpen, onClose, memberName, brief, onExportComplete }) => {
  const [selectedFormat, setSelectedFormat] = useState('pdf');
  const [includeSections, setIncludeSections] = useState({
    cognitiveStatus: true,
    workloadCapacity: true,
    burnoutRisk: true,
    performanceContext: true,
    growthDevelopment: true,
    conversationTopics: true
  });
  const [exporting, setExporting] = useState(false);
  const [exportSuccess, setExportSuccess] = useState(false);

  const formats = [
    { id: 'pdf', icon: FileText, title: 'PDF Document', desc: 'Formatted for printing or sharing. Includes all sections and AI insights.' },
    { id: 'summary', icon: FileText, title: 'Executive Summary', desc: 'One-page condensed version with key metrics and conversation topics only.' },
    { id: 'clipboard', icon: Clipboard, title: 'Copy to Clipboard', desc: 'Plain text version ready to paste into any tool.' }
  ];

  const sections = [
    { id: 'cognitiveStatus', label: 'Cognitive Status' },
    { id: 'workloadCapacity', label: 'Workload & Capacity' },
    { id: 'burnoutRisk', label: 'Burnout Risk Intelligence' },
    { id: 'performanceContext', label: 'Performance Context' },
    { id: 'growthDevelopment', label: 'Growth & Development' },
    { id: 'conversationTopics', label: 'Suggested Conversation Topics' }
  ];

  const generateBriefContent = () => {
    const date = new Date().toISOString().split('T')[0];
    let content = `CognitiveOS 1:1 Intelligence Brief\n`;
    content += `Generated: ${new Date().toLocaleString()}\n`;
    content += `Team Member: ${memberName}\n\n`;
    content += `${'='.repeat(50)}\n\n`;

    if (includeSections.cognitiveStatus && brief?.sections?.cognitiveStatus) {
      const cs = brief.sections.cognitiveStatus;
      content += `COGNITIVE STATUS\n`;
      content += `-----------------\n`;
      content += `Lane Alignment: ${cs.laneAlignmentScore}%\n`;
      content += `Primary Lane: ${cs.primaryLane?.name}\n`;
      content += `Level-Appropriate Work: ${cs.levelAppropriateWork}%\n`;
      content += `AI Insight: ${cs.aiSummary}\n\n`;
    }

    if (includeSections.workloadCapacity && brief?.sections?.workloadCapacity) {
      const wc = brief.sections.workloadCapacity;
      content += `WORKLOAD & CAPACITY\n`;
      content += `--------------------\n`;
      content += `Current Load: ${wc.currentLoad}/${wc.maxLoad} tasks (${wc.loadPercentage}%)\n`;
      content += `Active Tasks: ${wc.activeTasks}\n`;
      content += `Overdue Tasks: ${wc.overdueTasks}\n\n`;
    }

    if (includeSections.burnoutRisk && brief?.sections?.burnoutRisk) {
      const br = brief.sections.burnoutRisk;
      content += `BURNOUT RISK INTELLIGENCE\n`;
      content += `--------------------------\n`;
      content += `Risk Score: ${br.score}/100\n`;
      content += `Status: ${br.score >= 70 ? 'High Risk' : br.score >= 50 ? 'Elevated' : 'Healthy'}\n`;
      content += `AI Insight: ${br.aiInterpretation}\n\n`;
    }

    if (includeSections.performanceContext && brief?.sections?.performanceContext) {
      const pc = brief.sections.performanceContext;
      content += `PERFORMANCE CONTEXT\n`;
      content += `--------------------\n`;
      content += `On-Time Rate: ${pc.onTimeRate}%\n`;
      content += `Phases Completed: ${pc.phasesCompleted}\n`;
      if (pc.wins?.length > 0) {
        content += `Notable Wins:\n`;
        pc.wins.forEach(win => { content += `  - ${win}\n`; });
      }
      content += `\n`;
    }

    if (includeSections.conversationTopics && brief?.sections?.conversationTopics) {
      content += `SUGGESTED CONVERSATION TOPICS\n`;
      content += `------------------------------\n`;
      brief.sections.conversationTopics.forEach((topic, i) => {
        content += `${i + 1}. ${topic.text}\n`;
      });
      content += `\n`;
    }

    return content;
  };

  const handleExport = async () => {
    setExporting(true);
    
    await new Promise(resolve => setTimeout(resolve, 800));

    const content = generateBriefContent();
    const date = new Date().toISOString().split('T')[0];
    const safeName = memberName.replace(/\s+/g, '');
    const filename = `CognitiveOS-1on1-Brief-${safeName}-${date}`;

    if (selectedFormat === 'clipboard') {
      await navigator.clipboard.writeText(content);
    } else {
      // Create downloadable file
      const blob = new Blob([content], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = selectedFormat === 'pdf' ? `${filename}.txt` : `${filename}-summary.txt`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }

    setExporting(false);
    setExportSuccess(true);
    
    setTimeout(() => {
      setExportSuccess(false);
      onClose();
      onExportComplete?.();
    }, 2000);
  };

  if (!isOpen) return null;

  return (
    <div className="absolute inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden" data-testid="export-modal">
        {exportSuccess ? (
          <div className="p-12 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center">
              <Check className="w-8 h-8 text-green-500" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Brief exported successfully</h3>
            <p className="text-gray-500">
              {selectedFormat === 'clipboard' ? 'Copied to clipboard' : 'File downloaded'}
            </p>
          </div>
        ) : (
          <>
            <div className="p-6 border-b border-gray-100">
              <h3 className="text-lg font-bold text-gray-900">Export Brief — {memberName}</h3>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Format Selection */}
              <div className="grid grid-cols-3 gap-3">
                {formats.map(format => (
                  <button key={format.id} onClick={() => setSelectedFormat(format.id)}
                    className={`p-4 rounded-xl border-2 text-left transition-all ${
                      selectedFormat === format.id ? 'border-teal-500 bg-teal-50' : 'border-gray-200 hover:border-gray-300'
                    }`}>
                    <format.icon className={`w-6 h-6 mb-2 ${selectedFormat === format.id ? 'text-teal-500' : 'text-gray-400'}`} />
                    <p className="font-semibold text-sm text-gray-900">{format.title}</p>
                    <p className="text-xs text-gray-500 mt-1">{format.desc}</p>
                  </button>
                ))}
              </div>
              
              {/* Section Toggles */}
              <div>
                <p className="text-sm font-semibold text-gray-700 mb-3">Include in export</p>
                <div className="grid grid-cols-2 gap-2">
                  {sections.map(section => (
                    <label key={section.id} className="flex items-center gap-2 p-2 rounded-lg hover:bg-gray-50 cursor-pointer">
                      <input type="checkbox" checked={includeSections[section.id]}
                        onChange={e => setIncludeSections(prev => ({ ...prev, [section.id]: e.target.checked }))}
                        className="w-4 h-4 rounded border-gray-300 text-teal-500 focus:ring-teal-500" />
                      <span className="text-sm text-gray-700">{section.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
            
            <div className="p-6 bg-gray-50 border-t border-gray-100 flex justify-end gap-3">
              <button onClick={onClose}
                className="px-5 py-2.5 rounded-xl border border-gray-200 text-gray-700 font-medium hover:bg-gray-100">
                Cancel
              </button>
              <button onClick={handleExport} disabled={exporting}
                className="px-5 py-2.5 rounded-xl bg-teal-500 text-white font-medium hover:bg-teal-600 flex items-center gap-2 disabled:opacity-50">
                {exporting ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                Export
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// ================================================
// OPEN IN THREADS PANEL
// ================================================
const OpenInThreadsPanel = ({ isOpen, onClose, onCreateThread, memberName, topics }) => {
  const [threadTitle, setThreadTitle] = useState(`1:1 — ${memberName} — ${new Date().toLocaleDateString()}`);
  const [additionalNote, setAdditionalNote] = useState('');
  const [scheduledDate, setScheduledDate] = useState('');
  const [creating, setCreating] = useState(false);
  const [success, setSuccess] = useState(false);

  const agenda = topics?.slice(0, 5).map((t, i) => `${i + 1}. ${t.text}`).join('\n') || '';

  const handleCreate = async () => {
    setCreating(true);
    await new Promise(resolve => setTimeout(resolve, 1200));
    setCreating(false);
    setSuccess(true);
    
    setTimeout(() => {
      onCreateThread?.();
    }, 2000);
  };

  if (!isOpen) return null;

  return (
    <div className={`absolute right-0 top-0 bottom-0 w-96 bg-white shadow-2xl z-40 flex flex-col transition-transform duration-300 ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}
      data-testid="threads-panel">
      {success ? (
        <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
          <div className="w-16 h-16 mb-4 rounded-full bg-teal-100 flex items-center justify-center">
            <Check className="w-8 h-8 text-teal-500" />
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">Thread created</h3>
          <p className="text-gray-500 mb-4">{memberName} has been notified</p>
          <button onClick={onCreateThread} className="text-teal-500 font-medium hover:underline flex items-center gap-1">
            View Thread <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <>
          <div className="p-6 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <MessageSquare className="w-5 h-5 text-teal-500" />
                <h3 className="text-lg font-bold text-gray-900">Creating Thread</h3>
              </div>
            </div>
          </div>
          
          <div className="flex-1 overflow-y-auto p-6 space-y-5">
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1.5 block">Thread Title</label>
              <input type="text" value={threadTitle} onChange={e => setThreadTitle(e.target.value)}
                className="w-full p-3 rounded-xl border border-gray-200 focus:border-teal-500 focus:ring-1 focus:ring-teal-500 outline-none" />
            </div>
            
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1.5 block">Thread Type</label>
              <div className="p-3 rounded-xl bg-teal-50 border border-teal-200 text-teal-700 font-medium">
                1:1 Meeting
              </div>
            </div>
            
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1.5 block">Pre-built Agenda</label>
              <textarea value={agenda} readOnly rows={5}
                className="w-full p-3 rounded-xl border border-gray-200 bg-gray-50 text-sm text-gray-600 resize-none" />
            </div>
            
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1.5 block">Participants</label>
              <div className="flex items-center gap-2 p-3 rounded-xl bg-gray-50 border border-gray-200">
                <div className="w-8 h-8 rounded-full bg-teal-500 flex items-center justify-center text-white text-xs font-bold">
                  {memberName.split(' ').map(n => n[0]).join('')}
                </div>
                <span className="text-sm text-gray-700">{memberName}</span>
                <span className="text-gray-400 mx-1">&</span>
                <div className="w-8 h-8 rounded-full bg-gray-400 flex items-center justify-center text-white text-xs font-bold">
                  You
                </div>
                <span className="text-sm text-gray-700">You</span>
              </div>
            </div>
            
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1.5 block">Schedule</label>
              <input type="datetime-local" value={scheduledDate} onChange={e => setScheduledDate(e.target.value)}
                className="w-full p-3 rounded-xl border border-gray-200 focus:border-teal-500 focus:ring-1 focus:ring-teal-500 outline-none" />
            </div>
            
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1.5 block">Add a note</label>
              <textarea value={additionalNote} onChange={e => setAdditionalNote(e.target.value)}
                placeholder="Any additional context before sending..."
                rows={3} className="w-full p-3 rounded-xl border border-gray-200 focus:border-teal-500 focus:ring-1 focus:ring-teal-500 outline-none resize-none" />
            </div>
          </div>
          
          <div className="p-6 border-t border-gray-100 flex gap-3">
            <button onClick={onClose}
              className="flex-1 px-5 py-2.5 rounded-xl border border-gray-200 text-gray-700 font-medium hover:bg-gray-50">
              Back to Brief
            </button>
            <button onClick={handleCreate} disabled={creating}
              className="flex-1 px-5 py-2.5 rounded-xl bg-teal-500 text-white font-medium hover:bg-teal-600 flex items-center justify-center gap-2 disabled:opacity-50">
              {creating ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              Create Thread
            </button>
          </div>
        </>
      )}
    </div>
  );
};

// ================================================
// ENHANCED BRIEF HISTORY PANEL
// ================================================
const EnhancedBriefHistoryPanel = ({ isOpen, history, memberName, onClose, onSelectBrief, selectedBriefs, compareMode, onToggleCompare }) => {
  if (!isOpen) return null;

  return (
    <div className="w-2/5 border-l border-gray-200 bg-white flex flex-col" data-testid="brief-history-panel">
      <div className="p-5 border-b border-gray-100">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <History className="w-5 h-5 text-gray-600" />
            <h3 className="text-lg font-bold text-gray-900">Brief History — {memberName}</h3>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>
        
        {/* Compare Toggle */}
        <label className="flex items-center gap-2 p-2 rounded-lg bg-gray-50 cursor-pointer">
          <input type="checkbox" checked={compareMode} onChange={e => onToggleCompare(e.target.checked)}
            className="w-4 h-4 rounded border-gray-300 text-teal-500 focus:ring-teal-500" />
          <GitCompare className="w-4 h-4 text-gray-500" />
          <span className="text-sm text-gray-700">Compare mode</span>
          {compareMode && <span className="text-xs text-gray-400 ml-auto">Select 2 briefs</span>}
        </label>
      </div>
      
      <div className="flex-1 overflow-y-auto p-5">
        <div className="space-y-3">
          {history.map((item, idx) => {
            const isSelected = selectedBriefs.includes(item.id);
            const riskStatus = item.burnoutRisk >= 60 ? 'Elevated' : item.burnoutRisk >= 40 ? 'Moderate' : 'Healthy';
            const riskColor = item.burnoutRisk >= 60 ? '#EF4444' : item.burnoutRisk >= 40 ? '#F59E0B' : '#10B981';
            
            // Determine most significant change
            let changeIndicator = null;
            if (idx < history.length - 1) {
              const prev = history[idx + 1];
              const burnoutDiff = item.burnoutRisk - prev.burnoutRisk;
              const alignDiff = item.cognitiveStatus - prev.cognitiveStatus;
              
              if (Math.abs(burnoutDiff) > Math.abs(alignDiff)) {
                changeIndicator = {
                  direction: burnoutDiff > 0 ? 'up' : 'down',
                  metric: 'Burnout',
                  value: Math.abs(burnoutDiff),
                  isGood: burnoutDiff < 0
                };
              } else if (Math.abs(alignDiff) > 0) {
                changeIndicator = {
                  direction: alignDiff > 0 ? 'up' : 'down',
                  metric: 'Alignment',
                  value: Math.abs(alignDiff),
                  isGood: alignDiff > 0
                };
              }
            }
            
            return (
              <div key={item.id}
                className={`p-4 rounded-xl border-2 transition-all cursor-pointer ${
                  isSelected ? 'border-teal-500 bg-teal-50' : 'border-gray-100 hover:border-gray-200 hover:shadow-md'
                }`}
                onClick={() => onSelectBrief(item.id)}>
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <span className="text-sm font-semibold text-gray-900">
                      {new Date(item.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </span>
                    <span className="text-xs text-gray-400 ml-2">
                      {new Date(item.date).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                    </span>
                  </div>
                  {compareMode && (
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                      isSelected ? 'border-teal-500 bg-teal-500' : 'border-gray-300'
                    }`}>
                      {isSelected && <Check className="w-3 h-3 text-white" />}
                    </div>
                  )}
                </div>
                
                {/* Who generated */}
                <div className="flex items-center gap-2 mb-3 text-xs text-gray-500">
                  <User className="w-3 h-3" />
                  <span>Generated by {item.generatedBy || 'You'}</span>
                </div>
                
                {/* Snapshot metric */}
                <div className="p-3 rounded-lg bg-gray-50 mb-3">
                  <span className="text-sm">Burnout Risk: </span>
                  <span className="font-bold" style={{ color: riskColor }}>{item.burnoutRisk}</span>
                  <span className="text-sm text-gray-500"> — {riskStatus}</span>
                </div>
                
                {/* Change indicator */}
                {changeIndicator && (
                  <div className={`flex items-center gap-1 text-xs ${changeIndicator.isGood ? 'text-green-600' : 'text-red-600'}`}>
                    {changeIndicator.direction === 'up' ? 
                      <ArrowUp className="w-3 h-3" /> : 
                      <ArrowDown className="w-3 h-3" />
                    }
                    <span>{changeIndicator.metric} {changeIndicator.direction === 'up' ? '+' : '-'}{changeIndicator.value}% since previous</span>
                  </div>
                )}
                
                {!compareMode && (
                  <button className="mt-3 w-full py-2 rounded-lg bg-gray-100 text-gray-700 text-sm font-medium hover:bg-gray-200 flex items-center justify-center gap-2">
                    <Eye className="w-4 h-4" />
                    View
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>
      
      <div className="p-4 border-t border-gray-100 bg-gray-50">
        <p className="text-xs text-gray-500 leading-relaxed">
          Briefs are generated automatically before scheduled 1:1 meetings and can be manually generated at any time. All brief history is private to managers and executives.
        </p>
      </div>
    </div>
  );
};

// ================================================
// DEMO BRIEF HISTORY DATA
// ================================================
const generateDemoBriefHistory = (memberId) => {
  const baseDate = new Date();
  return [
    {
      id: `bh-${memberId}-1`,
      date: new Date(baseDate - 7 * 24 * 60 * 60 * 1000).toISOString(),
      cognitiveStatus: 72,
      burnoutRisk: 48,
      loadPercentage: 78,
      onTimeRate: 84,
      topicsCovered: 6,
      generatedBy: 'You'
    },
    {
      id: `bh-${memberId}-2`,
      date: new Date(baseDate - 14 * 24 * 60 * 60 * 1000).toISOString(),
      cognitiveStatus: 68,
      burnoutRisk: 62, // Elevated - this resolved
      loadPercentage: 85,
      onTimeRate: 79,
      topicsCovered: 5,
      generatedBy: 'You'
    },
    {
      id: `bh-${memberId}-3`,
      date: new Date(baseDate - 21 * 24 * 60 * 60 * 1000).toISOString(),
      cognitiveStatus: 75,
      burnoutRisk: 55,
      loadPercentage: 72,
      onTimeRate: 82,
      topicsCovered: 4,
      generatedBy: 'Sarah Chen'
    },
    {
      id: `bh-${memberId}-4`,
      date: new Date(baseDate - 35 * 24 * 60 * 60 * 1000).toISOString(),
      cognitiveStatus: 82, // Performance improvement
      burnoutRisk: 38,
      loadPercentage: 65,
      onTimeRate: 91,
      topicsCovered: 5,
      generatedBy: 'You'
    },
    {
      id: `bh-${memberId}-5`,
      date: new Date(baseDate - 42 * 24 * 60 * 60 * 1000).toISOString(),
      cognitiveStatus: 78,
      burnoutRisk: 42,
      loadPercentage: 70,
      onTimeRate: 88,
      topicsCovered: 6,
      generatedBy: 'You'
    }
  ];
};

// ================================================
// MAIN MODAL COMPONENT
// ================================================
const OneOnOnePrepModal = ({ isOpen, onClose, member: memberProp }) => {
  const [brief, setBrief] = useState(null);
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState([]);
  const [generatedAt, setGeneratedAt] = useState(null);
  
  // Action modals
  const [showExportModal, setShowExportModal] = useState(false);
  const [showThreadsPanel, setShowThreadsPanel] = useState(false);
  const [showFlagModal, setShowFlagModal] = useState(false);
  
  // History state
  const [selectedBriefs, setSelectedBriefs] = useState([]);
  const [compareMode, setCompareMode] = useState(false);
  
  // Normalize member prop
  const member = useMemo(() => {
    if (!memberProp) return null;
    if (typeof memberProp === 'string') {
      return {
        id: memberProp.toLowerCase().replace(/\s/g, '-'),
        name: memberProp,
        title: 'Team Member',
        role: 'Team Member',
        avatar: memberProp.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2),
        current_load: Math.floor(Math.random() * 6) + 2,
        max_task_load: 8,
        lane_proficiency: { operations: 'L3', creative: 'L2' }
      };
    }
    return memberProp;
  }, [memberProp]);
  
  // Keyboard shortcut
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen) {
        if (showExportModal) setShowExportModal(false);
        else if (showFlagModal) setShowFlagModal(false);
        else if (showThreadsPanel) setShowThreadsPanel(false);
        else if (showHistory) setShowHistory(false);
        else onClose();
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, showExportModal, showFlagModal, showThreadsPanel, showHistory, onClose]);
  
  useEffect(() => {
    if (isOpen && member) {
      setLoading(true);
      setTimeout(() => {
        const generatedBrief = generateBrief(member);
        setBrief(generatedBrief);
        setHistory(generateDemoBriefHistory(member.id));
        setGeneratedAt(new Date());
        setLoading(false);
      }, 800);
    }
  }, [isOpen, member]);
  
  const handleRegenerate = () => {
    setRegenerating(true);
    setTimeout(() => {
      const generatedBrief = generateBrief(member);
      setBrief(generatedBrief);
      setGeneratedAt(new Date());
      setRegenerating(false);
      toast.success('Brief refreshed with latest data');
    }, 1500);
  };
  
  const handleSelectBrief = (briefId) => {
    if (compareMode) {
      setSelectedBriefs(prev => {
        if (prev.includes(briefId)) {
          return prev.filter(id => id !== briefId);
        }
        if (prev.length < 2) {
          return [...prev, briefId];
        }
        return [prev[1], briefId];
      });
    }
  };
  
  if (!isOpen) return null;
  
  const primaryLane = brief?.member?.primaryLane;
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      
      {/* Modal */}
      <div className={`relative h-full max-h-[95vh] mx-4 bg-gray-50 rounded-2xl overflow-hidden shadow-2xl flex transition-all duration-300 ${
        showHistory || showThreadsPanel ? 'w-full max-w-7xl' : 'w-full max-w-6xl'
      }`} data-testid="one-on-one-prep-modal">
        
        {/* Main Content Area */}
        <div className={`flex flex-col transition-all duration-300 ${showHistory || showThreadsPanel ? 'w-3/5' : 'w-full'}`}>
          {/* Header */}
          <div className="relative p-8 flex-shrink-0"
            style={{ 
              background: primaryLane 
                ? `linear-gradient(135deg, ${primaryLane.color} 0%, ${primaryLane.color}CC 100%)`
                : 'linear-gradient(135deg, #1E293B 0%, #334155 100%)'
            }}>
            <div className="absolute inset-0 opacity-10">
              <div className="absolute inset-0" style={{
                backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
                backgroundSize: '24px 24px'
              }} />
            </div>
            
            <div className="relative flex items-start justify-between">
              <div className="flex items-center gap-6">
                <div className="w-20 h-20 rounded-2xl flex items-center justify-center text-2xl font-bold bg-white/20 backdrop-blur-sm text-white border-2 border-white/30">
                  {member?.avatar || member?.name?.split(' ').map(n => n[0]).join('')}
                </div>
                
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <h1 className="text-3xl font-black text-white">{member?.name}</h1>
                    {brief?.member?.primaryLevel && (
                      <span className="px-3 py-1 rounded-lg bg-white/20 backdrop-blur-sm text-white text-sm font-bold">
                        {brief.member.primaryLevel}
                      </span>
                    )}
                  </div>
                  <p className="text-white/80 text-lg">{member?.title}</p>
                  <div className="flex items-center gap-4 mt-3">
                    {primaryLane && (
                      <div className="flex items-center gap-2 px-3 py-1 rounded-lg bg-white/15 backdrop-blur-sm">
                        <div className="w-3 h-3 rounded-full bg-white" />
                        <span className="text-white/90 text-sm font-medium">{primaryLane.name}</span>
                      </div>
                    )}
                    <span className="text-white/60 text-sm">
                      {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
                    </span>
                  </div>
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                <button onClick={() => setShowHistory(true)}
                  className="p-3 rounded-xl bg-white/10 hover:bg-white/20 transition-colors" title="View Brief History">
                  <History className="w-5 h-5 text-white" />
                </button>
                <button onClick={onClose} className="p-3 rounded-xl bg-white/10 hover:bg-white/20 transition-colors">
                  <X className="w-5 h-5 text-white" />
                </button>
              </div>
            </div>
            
            <div className="absolute bottom-4 right-8 flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur-sm">
              <Sparkles className="w-4 h-4 text-white/80" />
              <span className="text-white/80 text-xs font-medium">Generated by CognitiveOS Intelligence</span>
            </div>
          </div>
          
          {/* Content */}
          <div className="flex-1 overflow-y-auto p-8">
            {loading || regenerating ? (
              <div className="flex items-center justify-center py-20">
                <div className="flex flex-col items-center gap-4">
                  <div className="w-16 h-16 rounded-full border-4 border-t-transparent animate-spin"
                    style={{ borderColor: `${primaryLane?.color || DESIGN.colors.primary}30`, borderTopColor: primaryLane?.color || DESIGN.colors.primary }} />
                  <p className="text-gray-500">{regenerating ? 'Refreshing intelligence...' : 'Generating intelligence brief...'}</p>
                </div>
              </div>
            ) : brief ? (
              <div className="grid grid-cols-2 gap-6">
                <CognitiveStatusSection data={brief.sections.cognitiveStatus} />
                <WorkloadCapacitySection data={brief.sections.workloadCapacity} />
                <BurnoutRiskSection data={brief.sections.burnoutRisk} />
                <PerformanceContextSection data={brief.sections.performanceContext} />
                <GrowthDevelopmentSection data={brief.sections.growthDevelopment} memberName={member?.name} />
                <ConversationTopicsSection topics={brief.sections.conversationTopics} />
              </div>
            ) : null}
          </div>
          
          {/* Footer Actions */}
          <div className="flex-shrink-0 p-6 bg-white border-t border-gray-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <button onClick={() => setShowHistory(true)}
                  className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 cursor-pointer">
                  <Clock className="w-4 h-4" />
                  <span>Brief generated at {generatedAt?.toLocaleTimeString() || new Date().toLocaleTimeString()}</span>
                </button>
                <span className="text-gray-300">|</span>
                <button onClick={handleRegenerate} disabled={regenerating}
                  className="text-sm text-teal-600 hover:text-teal-700 font-medium flex items-center gap-1 disabled:opacity-50">
                  <RefreshCw className={`w-3 h-3 ${regenerating ? 'animate-spin' : ''}`} />
                  Regenerate
                </button>
              </div>
              
              <div className="flex items-center gap-3">
                <button onClick={() => setShowExportModal(true)}
                  className="flex items-center gap-2 px-5 py-2.5 rounded-xl border border-gray-200 text-gray-700 font-medium hover:bg-gray-50 transition-colors"
                  data-testid="export-brief-btn">
                  <Download className="w-4 h-4" />
                  Export Brief
                </button>
                <button onClick={() => setShowThreadsPanel(true)}
                  className="flex items-center gap-2 px-5 py-2.5 rounded-xl border border-gray-200 text-gray-700 font-medium hover:bg-gray-50 transition-colors"
                  data-testid="open-in-threads-btn">
                  <MessageSquare className="w-4 h-4" />
                  Open in Threads
                </button>
                <button onClick={() => setShowFlagModal(true)}
                  className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-amber-500 text-white font-medium hover:bg-amber-600 transition-colors"
                  data-testid="flag-for-review-btn">
                  <Flag className="w-4 h-4" />
                  Flag for Executive Review
                </button>
              </div>
              
              <span className="text-xs text-gray-400">Esc to close</span>
            </div>
          </div>
        </div>
        
        {/* Brief History Panel */}
        {showHistory && (
          <EnhancedBriefHistoryPanel
            isOpen={showHistory}
            history={history}
            memberName={member?.name}
            onClose={() => setShowHistory(false)}
            onSelectBrief={handleSelectBrief}
            selectedBriefs={selectedBriefs}
            compareMode={compareMode}
            onToggleCompare={setCompareMode}
          />
        )}
        
        {/* Threads Panel */}
        {showThreadsPanel && (
          <OpenInThreadsPanel
            isOpen={showThreadsPanel}
            onClose={() => setShowThreadsPanel(false)}
            onCreateThread={() => {
              setShowThreadsPanel(false);
              toast.success('Thread created and notification sent');
            }}
            memberName={member?.name}
            topics={brief?.sections?.conversationTopics}
          />
        )}
        
        {/* Export Modal */}
        <ExportBriefModal
          isOpen={showExportModal}
          onClose={() => setShowExportModal(false)}
          memberName={member?.name}
          brief={brief}
          onExportComplete={() => toast.success('Brief exported successfully')}
        />
        
        {/* Flag Modal - Enhanced */}
        <EnhancedFlagModal
          isOpen={showFlagModal}
          onClose={() => setShowFlagModal(false)}
          member={member}
          brief={brief}
          onFlagComplete={() => toast.success('Flag submitted successfully')}
        />
      </div>
    </div>
  );
};

export default OneOnOnePrepModal;
